import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Loader2 } from "lucide-react";

const ACTIONS = [
  { key: "briefing", label: "Daily Briefing", path: "/office/ai/briefing" },
  { key: "followups", label: "Follow-up Summary", path: "/office/ai/follow-ups" },
  { key: "bottlenecks", label: "Bottleneck Detection", path: "/office/ai/bottlenecks" },
  { key: "workload", label: "Workload Analysis", path: "/office/ai/workload" },
];

export function OfficeAiPanel() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(null);
  const [q, setQ] = useState("");

  const run = async (a) => {
    setLoading(a.key);
    setResult(null);
    try {
      const r = await api.get(a.path);
      setResult({ label: a.label, ...r.data });
    } catch (e) {
      setResult({ label: a.label, ok: false, text: typeof e.response?.data?.detail === "string" ? e.response.data.detail : "AI unavailable" });
    } finally {
      setLoading(null);
    }
  };

  const ask = async () => {
    if (!q.trim()) return;
    setLoading("ask");
    setResult(null);
    try {
      const r = await api.post("/office/ai/ask", { question: q });
      setResult({ label: q, ...r.data });
    } catch (e) {
      setResult({ label: q, ok: false, text: typeof e.response?.data?.detail === "string" ? e.response.data.detail : "AI unavailable" });
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-3" data-testid="office-ai-panel">
      <h3 className="flex items-center gap-2 text-base font-semibold uppercase tracking-tight">
        <Sparkles className="w-4 h-4 text-primary" /> AI Task Assistant</h3>
      <div className="flex flex-wrap gap-2">
        {ACTIONS.map((a) => (
          <Button key={a.key} size="sm" variant="outline" disabled={!!loading} onClick={() => run(a)} data-testid={`ai-${a.key}`}>
            {loading === a.key ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}{a.label}
          </Button>))}
      </div>
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder='Ask about your tasks — e.g. "Summarize my pending tasks" or "Summarize my team’s overdue tasks and blockers"'
          data-testid="ai-ask-input" />
        <Button size="sm" className="bg-primary text-primary-foreground" disabled={!!loading || !q.trim()} onClick={ask} data-testid="ai-ask-submit">
          {loading === "ask" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Ask"}</Button>
      </div>
      {result && (
        <div className={`border rounded-md p-3 text-sm whitespace-pre-wrap ${result.ok ? "border-border" : "border-signal-amber/50 text-signal-amber"}`}
          data-testid="ai-result">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{result.label}</p>
          {result.text}
        </div>)}
    </div>
  );
}
